/* eslint-disable max-lines */
import React, { memo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAnchorElState, useBooleanState } from 'react-use-object-state';
import { FeatureEntitlementName } from '@jupiterone/entitlement-types';
import {
  APP_ACCESS_PERMISSIONS,
  Badge,
  EInvitationStatus,
  IInvitation,
  JupiterOneApps,
  JupiterTheme,
  EntitlementsFeatureTooltip,
  useFeatureFlags,
  useQueryString,
  useInviteClient,
  useAsyncEffect,
  useABAC,
  useAccounts,
} from '@jupiterone/jupiter-web-toolkit';
import { Menu, MenuProps, MenuItem, Tooltip } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import {
  AccountModalPage,
  AccountSettingsModal,
  SettingsPage,
} from 'navbar/src/jupiterAccountSettings';
import {
  BatteryCharging as PowerIcon,
  Clipboard as AuditIcon,
  Lock,
  Mail,
  Settings as GearIcon,
  Sliders,
  User as UserIcon,
  Users,
  Zap as ZapIcon,
  Bell as BellIcon,
} from 'react-feather';
import StorefrontIcon from '@material-ui/icons/Storefront';

const integrationAppAccessPermissions =
  APP_ACCESS_PERMISSIONS[JupiterOneApps.Integrations];
const endpointComplianceAppAccessPermissions =
  APP_ACCESS_PERMISSIONS[JupiterOneApps.EndpointCompliance];
const auditEventsAccessPermissions =
  APP_ACCESS_PERMISSIONS[JupiterOneApps.AuditEvents];

const DATA_TESTID_SETTINGS_MENU = 'navbar-settings-menu';

type MenuItem = {
  Icon: (props: any) => JSX.Element;
  iconProps?: object;
  title: string;
  entitlement?: FeatureEntitlementName;
  onClick: VoidFunction;
  render: boolean;
  dataTestId: string;
};

const isModalOpen = (keyValue: string): boolean => {
  return keyValue in SettingsPage;
};

const useStyles = makeStyles((theme: JupiterTheme) => {
  const NAV_ICON_SIZE = 28;
  const NAV_ICON_SIZE_SPACING = theme.spacing(2.5);
  return createStyles({
    settingsButton: {
      height: 28,
      '& svg': {
        width: NAV_ICON_SIZE,
        height: NAV_ICON_SIZE,
        marginLeft: NAV_ICON_SIZE_SPACING,
        cursor: 'pointer',
        '&.active': {
          color: theme.palette.primary.light,
        },
        '&:hover': {
          color: theme.palette.primary.light,
        },
        '&:focus': {
          outline: 'none',
        },
      },
    },
    menuItem: {
      justifyContent: 'flex-start',
      padding: 0,
    },
    subMenuItem: {
      justifyContent: 'flex-start',
      paddingRight: theme.spacing(3.25),
      paddingLeft: theme.spacing(3.25),
      '& a': {
        textDecoration: 'none',
        display: 'flex',
        color: 'inherit',
        '&:visited': {
          color: 'inherit',
        },
      },
      '& b': {
        color: theme.palette.coolGrey[400],
        transition: 'color 0.3s',
      },
      '&:hover': {
        backgroundColor: theme.palette.coolGrey[50],
        '& b': {
          color: theme.palette.primary.main,
        },
      },
    },
    menuIcon: {
      display: 'flex',
      color: theme.palette.primary.main,
      marginRight: theme.spacing(1.25),
      transition: 'color 0.3s',
      ':hover > &': {
        color: theme.palette.primary.main,
      },
    },
    settingsBadge: {
      position: 'absolute',
      right: '5px',
      bottom: '8px',
    },
  });
});

const SettingsMenu = ({ ...props }: Omit<MenuProps, 'open'>) => {
  const classes = useStyles();
  const { isEnterprise } = useAccounts();
  const { enforce } = useABAC();
  const isAdmin = enforce([]);
  const history = useHistory();
  const { hasFeature } = useFeatureFlags();
  const invitesPending = useBooleanState(false);
  const { getInvitations, loading } = useInviteClient();

  const [settingsPage, onSettingsPageChange] = useQueryString('settings', '');

  useAsyncEffect(async () => {
    if (loading) {
      return;
    }
    const response = await getInvitations();
    const invites = response.items.filter(
      (i: IInvitation) => i.status === EInvitationStatus.pending
    );
    invitesPending.setState(invites.length > 0);
  }, [loading]);

  const menu = useAnchorElState(null);

  const menuItems: MenuItem[] = [
    {
      Icon: UsersNAccessIcon,
      title: 'Users & Access',
      onClick: () => onSettingsPageChange('access'),
      render: isAdmin,
      dataTestId: `${DATA_TESTID_SETTINGS_MENU}-users-access`,
    },
    {
      Icon: SSOIcon,
      title: 'Single Sign On',
      onClick: () => onSettingsPageChange('clients'),
      entitlement: FeatureEntitlementName.SAML_SSO,
      render: true,
      dataTestId: `${DATA_TESTID_SETTINGS_MENU}-sso`,
    },
    {
      Icon: ProfileIcon,
      title: 'My Profile',
      onClick: () => onSettingsPageChange('profile'),
      render: true,
      dataTestId: `${DATA_TESTID_SETTINGS_MENU}-my-profile`,
    },
    {
      Icon: NotificationsIcon,
      title: 'Notifications',
      onClick: () => onSettingsPageChange('notifications'),
      render: hasFeature('notifications'),
      dataTestId: `${DATA_TESTID_SETTINGS_MENU}-notifications`,
    },
    {
      Icon: AccountManagementIcon,
      title: 'Account Management',
      onClick: () => onSettingsPageChange('accountManagement'),
      render: isAdmin,
      dataTestId: `${DATA_TESTID_SETTINGS_MENU}-account-management`,
    },
    {
      Icon: AuditEventsIcon,
      title: 'Audit Events',
      onClick: () => history.push('/audit-events'),
      render: enforce(auditEventsAccessPermissions),
      dataTestId: `${DATA_TESTID_SETTINGS_MENU}-audit-events`,
    },
    {
      Icon: InvitationsIcon,
      title: 'Invitations',
      iconProps: { invitesPending: invitesPending.state },
      onClick: () => {
        invitesPending.setFalse();
        onSettingsPageChange('invitations');
      },
      render: true,
      dataTestId: `${DATA_TESTID_SETTINGS_MENU}-invitations`,
    },
    {
      Icon: IntegrationsIcon,
      title: 'Integrations',
      onClick: () => history.push('/integrations'),
      render: enforce(integrationAppAccessPermissions),
      dataTestId: `${DATA_TESTID_SETTINGS_MENU}-integrations`,
    },
    {
      Icon: PowerUpIcon,
      title: 'Power Ups',
      onClick: () => history.push('/powerups'),
      entitlement: FeatureEntitlementName.POWER_UPS,
      // TODO: When more powerups get added, spread them here
      render: enforce(endpointComplianceAppAccessPermissions),
      dataTestId: `${DATA_TESTID_SETTINGS_MENU}-powerups`,
    },
    {
      Icon: StoreIcon,
      title: 'In-App Purchasing',
      onClick: () => history.push('/products'),
      render: !isEnterprise && isAdmin,
      dataTestId: `${DATA_TESTID_SETTINGS_MENU}-in-app-purchases`,
    },
  ];

  return (
    <>
      <div className={classes.settingsButton}>
        <Badge
          anchorOrigin={{
            horizontal: 'right',
            vertical: 'bottom',
          }}
          classes={{ badge: classes.settingsBadge }}
          invisible={invitesPending.state ? false : true}
          overlap="circle"
          size="SMALL"
          status={invitesPending.state ? 'ERROR' : undefined}
        >
          <Tooltip title="Settings" enterDelay={750} onClick={menu.setAnchorEl}>
            <div>
              <GearIcon
                data-testid="navbar-settings-menu"
                aria-label="Settings"
                aria-haspopup="true"
                className={menu.anchorEl ? 'active' : ''}
              />
            </div>
          </Tooltip>
        </Badge>
      </div>
      <Menu
        aria-label="Settings menu"
        anchorEl={menu.anchorEl}
        open={Boolean(menu.anchorEl)}
        disableAutoFocusItem={true}
        onClick={menu.clearAnchorEl}
        {...props}
      >
        {menuItems.map(
          ({
            render,
            title,
            Icon,
            onClick,
            entitlement,
            dataTestId,
            iconProps = {},
          }) => {
            return entitlement ? (
              <EntitlementsFeatureTooltip
                entitlement={entitlement}
                key={title}
                childrenContainerClass={classes.menuItem}
                placement="left"
              >
                {(hasEntitlement) =>
                  (hasEntitlement || render) && (
                    <MenuItem
                      data-testid={dataTestId}
                      className={classes.subMenuItem}
                      onClick={onClick}
                      disabled={!hasEntitlement}
                    >
                      <Icon {...iconProps} />
                      {title}
                    </MenuItem>
                  )
                }
              </EntitlementsFeatureTooltip>
            ) : (
              render && (
                <MenuItem
                  key={title}
                  onClick={onClick}
                  className={classes.subMenuItem}
                >
                  <Icon {...iconProps} />
                  {title}
                </MenuItem>
              )
            );
          }
        )}
      </Menu>

      <AccountSettingsModal
        open={isModalOpen(settingsPage)}
        onCloseModal={() => onSettingsPageChange('')}
        activePage={settingsPage as AccountModalPage}
        setActivePage={onSettingsPageChange}
      />
    </>
  );
};

const UsersNAccessIcon = () => {
  const classes = useStyles();
  return <Users className={classes.menuIcon} />;
};
const SSOIcon = () => {
  const classes = useStyles();
  return <Lock className={classes.menuIcon} />;
};
const ProfileIcon = () => {
  const classes = useStyles();
  return <UserIcon className={classes.menuIcon} />;
};
const AccountManagementIcon = () => {
  const classes = useStyles();
  return <Sliders className={classes.menuIcon} />;
};
const AuditEventsIcon = () => {
  const classes = useStyles();
  return <AuditIcon className={classes.menuIcon} />;
};
const IntegrationsIcon = () => {
  const classes = useStyles();
  return <ZapIcon className={classes.menuIcon} />;
};
const PowerUpIcon = () => {
  const classes = useStyles();
  return <PowerIcon className={classes.menuIcon} />;
};
const StoreIcon = () => {
  const classes = useStyles();
  return <StorefrontIcon className={classes.menuIcon} />;
};
const NotificationsIcon = () => {
  const classes = useStyles();
  return <BellIcon className={classes.menuIcon} />;
};
const InvitationsIcon = ({ invitesPending }: { invitesPending: boolean }) => {
  const classes = useStyles();
  return (
    <div className={classes.menuIcon}>
      <Badge
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        invisible={invitesPending ? false : true}
        overlap="circle"
        size="SMALL"
        status={invitesPending ? 'ERROR' : undefined}
      >
        <Mail />
      </Badge>
    </div>
  );
};
export default memo(SettingsMenu);
